import React, { createContext, useContext, useReducer } from 'react'

import { AGGREGATION_OPTIONS } from '../query-panel/utils.js'

const ACTIONS = {
  SET_PERMISSIONS: 'SET_PERMISSIONS',
  SET_SCHEMA: 'SET_SCHEMA',
  UPDATE_FIELD: 'UPDATE_FIELD',
  ADD_ATTRIBUTE: 'ADD_ATTRIBUTE',
  REMOVE_ATTRIBUTE: 'REMOVE_ATTRIBUTE',
  REMOVE_ALL_ATTRIBUTES: 'REMOVE_ALL_ATTRIBUTES',
  SET_DATA: 'SET_DATA',
  UPDATE_CLIENT: 'UPDATE_CLIENT',
  CLEAR_REDUCER: 'CLEAR_REDUCER',
  UPDATE_PREVIEW: 'UPDATE_PREVIEW',
  UPDATE_DELIVERY: 'UPDATE_DELIVERY',
  ADD_CUSTOM_ATTRIBUTE: 'ADD_CUSTOM_ATTRIBUTE',
  EDIT_CUSTOM_ATTRIBUTE: 'EDIT_CUSTOM_ATTRIBUTE',
  REMOVE_CUSTOM_ATTRIBUTE: 'REMOVE_CUSTOM_ATTRIBUTE',
  UPDATE_AGGREGATIONS: 'UPDATE_AGGREGATIONS',
  UPDATE_QUERY_SECTION_COLLAPSED: 'UPDATE_QUERY_SECTION_COLLAPSED',
  UPDATE_NAME: 'UPDATE_NAME',
  SET_SHOW_TAXONOMY_ERRORS: 'SET_SHOW_TAXONOMY_ERRORS',
}

const INITIAL_STATE = {
  schema: {
    filters: {},
    deliveries: {},
    sources: {},
    attributes: {},
  },
  data: {
    name: '',
    client: {},
    createdAt: '',
    attributes: [],
    filters: [],
    deliveries: [],
    sources: [],
    customAttributes: [],
    aggregations: [],
  },
  preview: {
    columns: [],
    rows: [],
  },
  querySectionsCollapsed: {
    aggregation: false,
    filter: false,
    attribute: false,
    customAttribute: false,
  },
  permissions: null,
  // Whether page-level Custom Taxonomy required-field errors should be shown.
  // Flipped on when the user tries to open the delivery modal with invalid fields.
  showTaxonomyErrors: false,
}

const reducer = (curState, action) => {
  switch (action.type) {
    case ACTIONS.SET_PERMISSIONS: {
      return { ...curState, permissions: action.payload }
    }

    case ACTIONS.SET_DATA: {
      const newDeliveries = getNormalizedDeliveries(action)

      return { ...curState, data: action.payload, deliveries: newDeliveries }
    }

    case ACTIONS.UPDATE_NAME: {
      return { ...curState, data: { ...curState.data, name: action.payload } }
    }

    case ACTIONS.CLEAR_REDUCER: {
      return INITIAL_STATE
    }

    case ACTIONS.SET_SCHEMA: {
      return { ...curState, schema: action.payload }
    }

    case ACTIONS.SET_SHOW_TAXONOMY_ERRORS: {
      return { ...curState, showTaxonomyErrors: action.payload }
    }

    case ACTIONS.UPDATE_DATA: {
      return { ...curState, data: { ...curState.data, ...action.payload } }
    }

    case ACTIONS.UPDATE_CLIENT: {
      return { ...curState, data: { ...curState.data, client: action.payload } }
    }

    case ACTIONS.UPDATE_PREVIEW: {
      return { ...curState, preview: { ...action.payload } }
    }

    case ACTIONS.UPDATE_FIELD: {
      return {
        ...curState,
        data: { ...curState.data, [action.payload.name]: action.payload.value },
      }
    }

    /**
     * Aggregation fields and attributes need to be on parity
     * If there's an aggregation, every attribute added that can be aggregated
     * should be. And every aggregated field should be added as an attribute
     */
    case ACTIONS.ADD_ATTRIBUTE: {
      const hasAggregationsOn = curState.data?.aggregations?.length > 0
      const canAttrBeAggregated = AGGREGATION_OPTIONS.includes(action.payload.attribute)

      return {
        ...curState,
        data: {
          ...curState.data,
          attributes: [...curState.data.attributes, action.payload.attribute],
          ...(hasAggregationsOn &&
            canAttrBeAggregated && {
              aggregations: [...curState.data.aggregations, action.payload.attribute],
            }),
        },
      }
    }

    /** If removed attribute is aggregated, remove it from aggregations as well,
     *  since there can't be aggregated fields without it being on attributes
     * */
    case ACTIONS.REMOVE_ATTRIBUTE: {
      const attributeSelected = curState.data.attributes[action.payload.index]
      const isAttributeAggregated = curState.data.aggregations.includes(attributeSelected)

      return {
        ...curState,
        data: {
          ...curState.data,
          attributes: curState.data.attributes.filter((_, index) => index !== action.payload.index),
          ...(isAttributeAggregated && {
            aggregations: curState.data.aggregations.filter(
              (aggregation) => !(aggregation === attributeSelected)
            ),
          }),
        },
      }
    }

    case ACTIONS.REMOVE_ALL_ATTRIBUTES: {
      return {
        ...curState,
        data: {
          ...curState.data,
          attributes: curState.data.attributes.filter((attribute) =>
            curState.data.aggregations.includes(attribute)
          ),
        },
      }
    }

    case ACTIONS.ADD_CUSTOM_ATTRIBUTE: {
      const result = {
        ...curState,
        data: {
          ...curState.data,
          customAttributes: [...curState.data.customAttributes, action.payload.customAttribute],
        },
      }
      return result
    }

    case ACTIONS.EDIT_CUSTOM_ATTRIBUTE: {
      return {
        ...curState,
        data: {
          ...curState.data,
          customAttributes: curState.data.customAttributes.map((ca) => {
            if (ca.alias === action.payload.alias) {
              return action.payload.customAttribute
            }
            return ca
          }),
        },
      }
    }

    case ACTIONS.REMOVE_CUSTOM_ATTRIBUTE: {
      return {
        ...curState,
        data: {
          ...curState.data,
          customAttributes: curState.data.customAttributes.filter(
            (attr) => attr.alias !== action.payload.alias
          ),
        },
      }
    }

    case ACTIONS.UPDATE_QUERY_SECTION_COLLAPSED: {
      return {
        ...curState,
        querySectionsCollapsed: {
          aggregation:
            action.payload.aggregation !== undefined
              ? !!action.payload.aggregation
              : curState.querySectionsCollapsed.aggregation,
          filter:
            action.payload.filter !== undefined
              ? !!action.payload.filter
              : curState.querySectionsCollapsed.filter,
          attribute:
            action.payload.attribute !== undefined
              ? !!action.payload.attribute
              : curState.querySectionsCollapsed.attribute,
          customAttribute:
            action.payload.customAttribute !== undefined
              ? !!action.payload.customAttribute
              : curState.querySectionsCollapsed.customAttribute,
        },
      }
    }

    case ACTIONS.UPDATE_DELIVERY: {
      return {
        ...curState,
        data: {
          ...curState.data,
          deliveries: [{ ...(curState.data.deliveries[0] || {}), ...action.payload }],
        },
      }
    }

    // Make sure all aggregated options selected on attributes are aggregated if adding any aggregations
    case ACTIONS.UPDATE_AGGREGATIONS: {
      const isAdding = action.payload.aggregations.length > curState.data?.aggregations?.length
      const attributesSelected = action.payload.attributes
      const aggregatedAttributesSelected = attributesSelected.filter((attr) =>
        AGGREGATION_OPTIONS.includes(attr)
      )

      if (isAdding) {
        const newAggregations = [...aggregatedAttributesSelected, ...action.payload.aggregations]
        return {
          ...curState,
          data: {
            ...curState.data,
            aggregations: [...new Set(newAggregations)],
            attributes: [...action.payload.attributes],
            projectOutputByFields:
              newAggregations.length > 0 ? curState.data.projectOutputByFields || false : false,
          },
        }
      }

      const newAttributes = action.payload.attributes.filter((attr) => {
        // If attribute selected can be aggregated and it's not on the new list of aggregations, remove it from attr.
        return !(AGGREGATION_OPTIONS.includes(attr) && !action.payload.aggregations.includes(attr))
      })

      const newAggregations = [...action.payload.aggregations]

      // If an aggregation is being removed
      // eslint-disable-next-line no-unused-vars
      const result = {
        ...curState,
        data: {
          ...curState.data,
          aggregations: newAggregations,
          attributes: newAttributes,
          projectOutputByFields:
            newAggregations.length > 0 ? curState.data.projectOutputByFields || false : null,
        },
      }

      return result
    }

    default:
      return curState
  }
}

const EventFeedsContext = createContext(INITIAL_STATE)

export const useEventFeedsContext = () => {
  const context = useContext(EventFeedsContext)

  return context
}

export const EventFeedsContextProvider = ({ children, id }) => {
  const [
    { schema, data, preview, querySectionsCollapsed, permissions, showTaxonomyErrors },
    dispatch,
  ] = useReducer(reducer, INITIAL_STATE)

  return (
    <EventFeedsContext.Provider
      value={{
        schema,
        data,
        preview,
        querySectionsCollapsed,
        id,
        dispatch,
        ACTIONS,
        permissions,
        showTaxonomyErrors,
      }}
    >
      {children}
    </EventFeedsContext.Provider>
  )
}

export const SAMPLING_SUPPORT = ['sharethis', 'sovrn', 'sovrneu']

function getNormalizedDeliveries(action) {
  // Check if there's a source filter there shouldn't be a source not included in the filter inside deliveries[0].datasourceSampling
  if (action.payload.filters.find((filter) => filter.key === 'source')) {
    const filteredSources =
      action.payload.filters.find((filter) => filter.key === 'source')?.value || []

    const sampledSources = SAMPLING_SUPPORT.filter((source) => filteredSources.includes(source))

    const newDatasourceSampling = sampledSources.reduce((acc, source) => {
      return {
        ...acc,
        [source]: action.payload.deliveries?.[0]?.datasourceSampling?.[source] ?? 1,
      }
    }, {})

    return action.payload.deliveries.map((delivery) => {
      return { ...delivery, datasourceSampling: newDatasourceSampling }
    })
  }

  return action.payload.deliveries.map((delivery) => ({
    ...delivery,
    datasourceSampling: {
      sharethis: 1,
      sovrn: 1,
      sovrneu: 1,
    },
  }))
}
